/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  THERAPIST_DOCUMENT_TYPES,
  UploadTherapistDocumentSchema,
  type TherapistDocumentType,
} from '@therapy/shared-validators';
import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentList } from '../../components/documents/document-list.js';
import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  type TherapistDocument,
  therapistDocumentsApi,
} from '../../lib/api/therapist-documents.js';
import {
  useDeleteTherapistDocument,
  useTherapistDocuments,
  useUploadTherapistDocument,
} from '../../lib/queries/therapist-documents.js';

const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';
const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      reject(new Error('Could not read the file'));
    };
    reader.readAsDataURL(file);
  });
}

function UploadForm({ therapistId }: { readonly therapistId: string }): JSX.Element {
  const { t } = useTranslation();
  const upload = useUploadTherapistDocument(therapistId);
  const [docType, setDocType] = useState<TherapistDocumentType>('contract');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onType = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setDocType(e.target.value as TherapistDocumentType);
  }, []);
  const onTitle = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);
  const onFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  }, []);
  const onSubmit = useCallback(() => {
    void (async () => {
      setError(null);
      if (file === null) {
        setError(t('documents.noFile'));
        return;
      }
      const dataUrl = await fileToDataUrl(file);
      const parsed = UploadTherapistDocumentSchema.safeParse({
        docType,
        title,
        fileName: file.name,
        dataUrl,
      });
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? t('common.saveError'));
        return;
      }
      await upload.mutateAsync(parsed.data);
      setTitle('');
      setFile(null);
    })();
  }, [file, docType, title, upload, t]);

  return (
    <div className="space-y-3 rounded-xl border border-dashed border-slate-300 p-3 dark:border-slate-600">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="doc-type" className={LABEL}>
            {t('documents.typeLabel')}
          </label>
          <select id="doc-type" className={INPUT} value={docType} onChange={onType}>
            {THERAPIST_DOCUMENT_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`documents.type.${value}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="doc-title" className={LABEL}>
            {t('documents.titleLabel')}
          </label>
          <input id="doc-title" type="text" className={INPUT} value={title} onChange={onTitle} />
        </div>
      </div>
      <div>
        <label htmlFor="doc-file" className={LABEL}>
          {t('documents.fileLabel')}
        </label>
        <input
          id="doc-file"
          type="file"
          accept=".pdf,image/png,image/jpeg,image/webp"
          className={INPUT}
          onChange={onFile}
        />
      </div>
      {error !== null && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
      <Button type="button" onClick={onSubmit} disabled={upload.isPending}>
        {t('documents.uploadCta')}
      </Button>
    </div>
  );
}

/** Admin → Therapist Profile → Documents (CR-012): upload + list + delete. */
export function TherapistDocumentsPanel({
  therapistId,
}: {
  readonly therapistId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const query = useTherapistDocuments(therapistId);
  const del = useDeleteTherapistDocument(therapistId);
  const onDownload = useCallback(
    (doc: TherapistDocument) => {
      void therapistDocumentsApi.download(therapistId, doc);
    },
    [therapistId],
  );
  const onDelete = useCallback(
    (id: string) => {
      del.mutate(id);
    },
    [del],
  );

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-slate-800 dark:text-white">{t('documents.title')}</p>
      <UploadForm therapistId={therapistId} />
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('documents.empty')}
      >
        <DocumentList docs={query.data ?? []} onDownload={onDownload} onDelete={onDelete} />
      </QueryState>
    </div>
  );
}
